import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Inventory,
  ShoppingCart,
  AttachMoney,
  People,
  Warning,
  CheckCircle,
  Category,
  LocalShipping,
} from '@mui/icons-material';
import { useGetProductsQuery } from '../../../store/api/productsApi';

function Dashboard() {
  const { data: productsData } = useGetProductsQuery({ limit: 100 });
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalSales: 0,
    totalPurchases: 0,
    lowStockItems: 0,
    activeProducts: 0,
    totalCategories: 0,
  });

  useEffect(() => {
    if (productsData?.products) {
      const products = productsData.products;
      setStats((prevStats) => ({
        totalProducts: products.length,
        activeProducts: products.filter((p) => p.isActive).length,
        lowStockItems: Math.floor(products.length * 0.1), // Mock data
        totalCategories: new Set(products.map((p) => p.categoryId).filter(Boolean)).size,
        totalSales: prevStats.totalSales || 0,
        totalPurchases: prevStats.totalPurchases || 0,
      }));
    }
  }, [productsData]);

  // Initialize with default values to prevent undefined errors
  const safeStats = useMemo(() => ({
    totalProducts: stats.totalProducts || 0,
    totalSales: stats.totalSales || 0,
    totalPurchases: stats.totalPurchases || 0,
    lowStockItems: stats.lowStockItems || 0,
    activeProducts: stats.activeProducts || 0,
    totalCategories: stats.totalCategories || 0,
  }), [stats]);

  const statCards = useMemo(() => [
    {
      title: 'Total Products',
      value: safeStats.totalProducts,
      icon: <Inventory sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      change: '+12%',
      trend: 'up',
    },
    {
      title: 'Total Sales',
      value: `$${safeStats.totalSales.toLocaleString()}`,
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      change: '+8%',
      trend: 'up',
    },
    {
      title: 'Total Purchases',
      value: `$${safeStats.totalPurchases.toLocaleString()}`,
      icon: <AttachMoney sx={{ fontSize: 40 }} />,
      color: '#ed6c02',
      change: '+5%',
      trend: 'up',
    },
    {
      title: 'Low Stock Items',
      value: safeStats.lowStockItems,
      icon: <Warning sx={{ fontSize: 40 }} />,
      color: '#d32f2f',
      change: '-3%',
      trend: 'down',
    },
    {
      title: 'Active Products',
      value: safeStats.activeProducts,
      icon: <CheckCircle sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      change: '+2%',
      trend: 'up',
    },
    {
      title: 'Categories',
      value: safeStats.totalCategories,
      icon: <Category sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
      change: '+1',
      trend: 'up',
    },
  ], [safeStats]);

  const recentActivities = [
    { action: 'New product added', item: 'Product ABC-123', time: '2 hours ago' },
    { action: 'Purchase order created', item: 'PO-2024-001', time: '5 hours ago' },
    { action: 'Sales order completed', item: 'SO-2024-045', time: '1 day ago' },
    { action: 'Stock adjusted', item: 'Location: Main Warehouse', time: '2 days ago' },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 4 }}>

        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#2C3E50' }}>
          Dashboard
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={index}>
            <Card
              sx={{
                height: '100%',
                background: 'white',
                border: 'none',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#7F8C8D',
                        fontWeight: 500,
                        mb: 1,
                        fontSize: '0.75rem',
                      }}
                    >
                      {stat.title}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: '#2C3E50',
                        mb: 1.5,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {stat.trend === 'up' ? (
                        <TrendingUp sx={{ fontSize: 16, color: '#4ECDC4', mr: 0.5 }} />
                      ) : (
                        <TrendingDown sx={{ fontSize: 16, color: '#F44336', mr: 0.5 }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          color: stat.trend === 'up' ? '#4ECDC4' : '#F44336',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      >
                        {stat.change}
                      </Typography>
                    </Box>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: '#4ECDC4',
                      width: 56,
                      height: 56,
                      boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)',
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper
            sx={{
              p: 4,
              height: '100%',
              background: 'white',
              border: 'none',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3, color: '#2C3E50' }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    background: 'white',
                    border: 'none',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <Inventory sx={{ fontSize: 48, color: '#4ECDC4', mb: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#2C3E50' }}>
                    Add Product
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    background: 'white',
                    border: 'none',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <ShoppingCart sx={{ fontSize: 48, color: '#4ECDC4', mb: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#2C3E50' }}>
                    New Sale
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    background: 'white',
                    border: 'none',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <LocalShipping sx={{ fontSize: 48, color: '#4ECDC4', mb: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#2C3E50' }}>
                    Purchase Order
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    background: 'white',
                    border: 'none',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <Category sx={{ fontSize: 48, color: '#4ECDC4', mb: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#2C3E50' }}>
                    Manage Categories
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    background: 'white',
                    border: 'none',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <Inventory sx={{ fontSize: 48, color: '#4ECDC4', mb: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#2C3E50' }}>
                    Stock Adjustment
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    background: 'white',
                    border: 'none',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <People sx={{ fontSize: 48, color: '#4ECDC4', mb: 1.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#2C3E50' }}>
                    Customers
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Recent Activities */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper
            sx={{
              p: 4,
              height: '100%',
              background: 'white',
              border: 'none',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3, color: '#2C3E50' }}>
              Recent Activities
            </Typography>
            <List>
              {recentActivities.map((activity, index) => (
                <ListItem
                  key={index}
                  sx={{
                    mb: 2,
                    borderRadius: 3,
                    bgcolor: '#F8F9FA',
                    px: 2,
                    py: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#E8F5E9',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        bgcolor: '#4ECDC4',
                        width: 36,
                        height: 36,
                        boxShadow: '0 2px 8px rgba(78, 205, 196, 0.3)',
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 20, color: 'white' }} />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                        {activity.action}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block" sx={{ color: '#2C3E50', mt: 0.5 }}>
                          {activity.item}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#7F8C8D', fontSize: '0.7rem' }}>
                          {activity.time}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Low Stock Alert */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              p: 4,
              background: 'white',
              border: 'none',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3, color: '#2C3E50' }}>
              Low Stock Alert
            </Typography>
            <Box>
              {[1, 2, 3, 4].map((item) => (
                <Box
                  key={item}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 3,
                    bgcolor: '#F8F9FA',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#E8F5E9',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#2C3E50' }}>
                      Product {item}
                    </Typography>
                    <Chip
                      label="Low Stock"
                      sx={{
                        bgcolor: '#FFC107',
                        color: '#2C3E50',
                        fontWeight: 600,
                        borderRadius: 2,
                      }}
                      size="small"
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.random() * 30}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: '#E0E0E0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        bgcolor: '#FFC107',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: 'block', color: '#7F8C8D', fontSize: '0.75rem' }}
                  >
                    Current: {Math.floor(Math.random() * 50)} / Reorder: 100
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Sales Overview */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              p: 4,
              background: 'white',
              border: 'none',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3, color: '#2C3E50' }}>
              Sales Overview
            </Typography>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 1,
                }}
              >
                $0
              </Typography>
              <Typography variant="body2" sx={{ color: '#7F8C8D', mb: 3 }}>
                Total Sales This Month
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ color: '#7F8C8D' }}>
                  No sales data available yet
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
